import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Check, X, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { Textarea } from "@/components/ui/textarea";
import { useApprovedRequestMutation, useRejectedRequestMutation, useRejectRefundRequestMutation } from "@/services/walletApi";
import { RefundRequest, WalletRequest, WalletRequestStatus } from "@/types/wallet";
import warning from "@/assets/warning.svg";
import { Label } from "@/components/ui/label";
import { getTokenBalance, mintToken } from "@/components/admin/pos/sell";
import { useDispatch } from "react-redux";
// import { setInvoice } from "@/features/invoice/invoiceSlice";
import { setWalletInvoice } from "@/redux/walletRefundSlice";
import { useNavigate } from "react-router-dom";
import { Gender, InvoiceStatus, InvoiceType, POSProductType, SaleStatus } from "@/types/enums";
import { useLazyGetMemberByIdQuery } from "@/services/memberApi";
import { useGetAllProductsQuery } from "@/services/productApi";
import { ProductType } from "@/types/system-setting/product";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";
import { useGetAllExtensionsQuery } from "@/services/extensionApi";

export function DataTableRowActions({
  data,
  refetch,
  access,
}: {
  data: RefundRequest;
  // data:any;
  refetch: () => void;
  access: string;
}) {
  const { toast } = useToast();
  const { userInfo } = useSelector((state: RootState) => state.auth);

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const { data: orgTaxType } = useGetOrgTaxTypeQuery(orgId) || {
      tax_type: "exclusive",
    };
  
  const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);
  const jwtToken = localStorage.getItem("userToken") || "";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: productList } = useGetAllProductsQuery({
      org_id: orgId,
      query: "",
    });
  const { data: enabledPayments } = useGetAllExtensionsQuery({org_id: orgId, query: `type=payment&for_admin=true&is_active=true`});

  const [
    getMember,
    { data: selectMemberData, isLoading: isMemberLoading },
  ] = useLazyGetMemberByIdQuery();

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isApproveOpen, setIsApproveOpen] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [isProcessingApproval, setIsProcessingApproval] = React.useState(false);

  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectRefundRequestMutation();

  // Handle reject transaction
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please provide a reason for rejection.",
      });
      return;
    }

    try {
      const resp = await rejectRequest({
        id: data.id,
        status: WalletRequestStatus.REJECTED,
        rejection_reason: rejectionReason,
        org_id: orgId,
      }).unwrap();

      toast({
        variant: "success",
        title: "Transaction Rejected",
        description: "The wallet transaction has been rejected.",
      });

      refetch();
      setIsDeleteOpen(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Error rejecting transaction", error);
      handleApiError(error);
    }
  };

  // Error handler
  const handleApiError = (error: any) => {
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError.data?.detail || typedError.data?.message || "Something went wrong."}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong. Please try again.",
      });
    }
  };

  // Handle text input to ensure only single spaces
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Replace multiple spaces with a single space
    const formattedText = value.replace(/\s+/g, " ");

    // Apply character limit (150 chars)
    if (formattedText.length <= 150) {
      setRejectionReason(formattedText);
    }
  };

  const handlePay = async () => {
    // First get member details
   const memberData= await getMember({
      id: data.user_id,
      org_id: orgId
    }).unwrap();
  
    // Filter for cash payment method only
    const cashPayment = enabledPayments?.data?.find(
      (payment) => payment.name.toLowerCase() === "cash"
    );
  
    if(!cashPayment) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Cash payment method not enabled",
      });
      return;
    }
    // Find wallet product from product list
    const walletProduct = productList?.data?.find(
      (product) => product.type ===ProductType.Wallet && product.flexible_price
    );

    if(!walletProduct) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Wallet product not found with flexible price",
      });
      return;
    }
  
    if (!memberData || !cashPayment || !walletProduct) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Required data not found",
      });
      return;
    }
    
    // Check if user has sufficient wallet balance before proceeding with refund
    if (memberData.wallet_address) {
      try {
        setIsProcessingApproval(true);
        const balanceData = await getTokenBalance({
          userAddress: memberData.wallet_address,
          orgId: orgId
        });
        
        if (balanceData.balance < Number(data.amount)) {
          toast({
            variant: "destructive",
            title: "Insufficient Balance",
            description: `User balance ${balanceData.balance} is lower than refund request amount ${data.amount}`,
          });
          setIsProcessingApproval(false);
          return;
        }
      } catch (error) {
        console.error("Failed to check wallet balance:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to verify wallet balance. Please try again.",
        });
        setIsProcessingApproval(false);
        return;
      }
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Member has no wallet address",
      });
      return;
    }
  
    const salesTax = 0;
    dispatch(
      setWalletInvoice({
        mainRefund_id: data.id,
        member_id: memberData.id,
        member_name: `${memberData.first_name} ${memberData.last_name}`, // Fixed string concatenation
        member_email: memberData.email ?? "",
        member_address: memberData.address_1 ?? "",
        member_nic: memberData.nic ?? "",
        member_gender: memberData.gender as Gender,
        notes: "wallet amount refunded",
        status: InvoiceStatus.Paid,
        staff_id: userInfo?.user?.id ?? 0,
        staff_name: userInfo?.user?.first_name ?? "",
        payment_date: new Date().toISOString().substring(0, 10),
        total: Number(data.amount),
        sub_total: Number(data.amount),
        tax_amount: 0,
        discount: 0,
        invoice_date: new Date().toISOString().substring(0, 10),
        total_quantity: 1,
        items: [
          {
            item_id: Number(walletProduct.id),
            item_type: POSProductType.Product,
            income_category_id: Number(walletProduct.income_category_id),
            description: "Wallet Refund",
            quantity: 1,
            price: Number(data.amount),
            tax_rate: 0,
            discount: 0,
            sale_tax_id: salesTax,
            sub_total: Number(data.amount), 
            tax_type: orgTaxType?.tax_type || "exclusive",
            tax_name: "default",
            total: Number(data.amount),
            tax_amount: 0,
            booking_date: new Date().toISOString(),
          },
        ],
        created_at: new Date().toISOString(),
        counter_id: 0,
        batch_id: 0,
        transaction_type: SaleStatus.Refund,
        payments: [
          {
            payment_method_id: Number(cashPayment.id),
            payment_method: "Cash",
            amount: Number(data.amount),
          },
        ],
        change_amount: 0,
        amount_received: 0,
        receipt_number: "",
      })
    );
    setTimeout(() => {
      navigate('/counter-selection')
    }, 500);
  };
  
  return (
    <>
      {/* Dropdown Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          onClick={(e) => e.stopPropagation()}
        >
          
          {data.status === WalletRequestStatus.PENDING && (
            <>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handlePay();
                }}
              >
                <Check className="mr-2 h-4 w-4 text-green-500" />
                Approve
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteOpen(true);
                }}
              >
                <X className="mr-2 h-4 w-4 text-red-500" />
                Reject
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        open={isDeleteOpen}
        // onOpenChange={setIsDeleteOpen}
        onOpenChange={(open) => {
          if (open === false && rejectionReason.trim() === "") {
            return;
          }
          setIsDeleteOpen(open);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogDescription>
              <div className="flex flex-col items-center justify-center gap-4">
                <img src={warning} alt="warning" className="w-18 h-18" />
                <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                  Reject this wallet transaction?
                </AlertDialogTitle>
              </div>
              <div className="my-4">
                <Label htmlFor="reason" className="text-sm font-medium">
                  Rejection Reason
                </Label>
                <Textarea
                  id="reason"
                  className="mt-2"
                  placeholder="Please provide a reason for rejection"
                  value={rejectionReason}
                  onChange={handleTextChange}
                  rows={4}
                  maxLength={150}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {rejectionReason.length}/150 characters
                </div>
              </div>
              <div className="w-full flex justify-between items-center gap-3 mt-4">
                <AlertDialogCancel
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteOpen(false);
                    setRejectionReason("");
                  }}
                  disabled={isRejecting}
                  className="w-full border border-primary font-semibold"
                >
                  <i className="fa fa-xmark text-base px-1"></i>
                  Cancel
                </AlertDialogCancel>
                {/* <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject();
                  }}
                  disabled={isRejecting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </Button> */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!rejectionReason.trim()) {
                      toast({
                        variant: "destructive",
                        title: "Error",
                        description: "Please provide a reason for rejection.",
                      });
                      return;
                    }
                    handleReject();
                  }}
                  disabled={isRejecting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </Button>{" "}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}